import React, { useMemo, useState } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';

import { LaneTypes } from '../../../constants';
import { useHasAccess } from '../../../hooks/useHasAccess';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { Event } from '../../../interfaces';
import { humanizeEnum } from '../../../utils';
import { ButtonsBox } from '../buttonsBox';
import { Spacer } from '../spacer';
import { ImageMetadataSettingsSelectorWrapper } from './atoms';
import { ImageMetadataSettings } from './types';

const lsImageMetadataSettingsKey = 'imageMetadataSettings';

export const useMetadataControls = (event: Event) => {
  const hasAccess = useHasAccess();

  const [visibleLanes, setVisibleLanes] = useLocalStorage('visibleLanes', []);

  const onLaneClick = (lane: string) => (evt: React.MouseEvent) => {
    if (visibleLanes.includes(lane)) {
      setVisibleLanes(visibleLanes.filter((l) => l !== lane));
    } else {
      setVisibleLanes([...visibleLanes, lane]);
    }
  };

  // Read once from localStorage and reuse across renders. Reading every render
  // also re-parses on every keystroke during a review session.
  const storedMetadataDisplaySettings = useMemo(
    () => JSON.parse(window.localStorage.getItem(lsImageMetadataSettingsKey) || '{}'),
    [],
  );

  const getDefaultState = (key: keyof ImageMetadataSettings) => {
    return storedMetadataDisplaySettings[key] === undefined ? true : storedMetadataDisplaySettings[key];
  };

  // Hooks must be called unconditionally and in stable order. Previously these
  // useState calls were nested inside an object literal which is technically
  // fine but easy to break; hoist them so the order is obvious.
  const drawLanesState = useState(getDefaultState('drawLanes'));
  const drawRoadEdgesState = useState(getDefaultState('drawRoadEdges'));
  const drawDetectionsState = useState(getDefaultState('drawDetections'));
  const drawLprPovState = useState(getDefaultState('drawLprPov'));
  const drawDetectNetState = useState(getDefaultState('drawDetectNet'));
  const drawSegNetState = useState(getDefaultState('drawSegNet'));
  const drawCarBoxState = useState(getDefaultState('drawCarBox'));

  const imageMetadataSettings: ImageMetadataSettings = {
    drawLanes: {
      description: 'Draw lanes',
      show: event.metadata?.lane_data?.lanes,
      state: drawLanesState,
    },
    drawRoadEdges: {
      description: 'Draw road edges',
      show: event.metadata?.lane_data?.keypoints?.road_edge_left,
      state: drawRoadEdgesState,
    },
    drawDetections: {
      description: 'Draw detections',
      show: event.metadata?.lane_data?.detections?.length > 0,
      state: drawDetectionsState,
    },
    drawLprPov: {
      description: 'Draw LPR camera point of view',
      show: event.metadata?.homography,
      state: drawLprPovState,
    },
    drawDetectNet: {
      description: 'Draw detectNet',
      show: event.metadata?.lane_data?.detectnet_poly?.length > 0,
      state: drawDetectNetState,
    },
    drawSegNet: {
      description: 'Draw segNet',
      show: event.metadata?.lane_data?.segnet_poly?.length > 0,
      state: drawSegNetState,
    },
    drawCarBox: {
      description: 'Draw car box',
      show: event.metadata?.car_detection?.front_slice?.length > 0,
      state: drawCarBoxState,
    },
  };

  const checkboxChangeFactory = (fieldName: keyof ImageMetadataSettings) => () => {
    const setter = imageMetadataSettings[fieldName].state[1];
    const value = imageMetadataSettings[fieldName].state[0];
    storedMetadataDisplaySettings[fieldName] = !value;
    window.localStorage.setItem(lsImageMetadataSettingsKey, JSON.stringify(storedMetadataDisplaySettings));
    setter(!value);
  };

  const getImageMetadataSettingsButtonValue = (fieldName: keyof ImageMetadataSettings) => {
    return imageMetadataSettings[fieldName].state[0];
  };

  const getImageWithMetadataParameters = () => {
    const parameters: any = {};
    for (const key in imageMetadataSettings) {
      parameters[key] = hasAccess && getImageMetadataSettingsButtonValue(key as keyof ImageMetadataSettings);
    }
    return parameters;
  };

  function MetadataControls() {
    const filteredKeys = Object.keys(imageMetadataSettings).filter((key) => imageMetadataSettings[key as keyof ImageMetadataSettings].show);
    if (filteredKeys.length) {
      return (
        <ImageMetadataSettingsSelectorWrapper>
          <ButtonGroup>
            {filteredKeys.map((key: any) => (
              <Button
                key={key}
                style={{ marginTop: 5, textTransform: 'capitalize' }}
                variant={getImageMetadataSettingsButtonValue(key) ? 'primary' : 'secondary'}
                value={getImageMetadataSettingsButtonValue(key)}
                onClick={checkboxChangeFactory(key)}
              >
                {imageMetadataSettings[key as keyof ImageMetadataSettings].description}
              </Button>
            ))}
          </ButtonGroup>

          {getImageMetadataSettingsButtonValue('drawLanes') && (
            <>
              <Spacer half />
              <ButtonsBox>
                {LaneTypes.map((v) => (
                  <Button key={v} onClick={onLaneClick(v)} variant={visibleLanes.includes(v) ? 'primary' : 'secondary'}>
                    {humanizeEnum(v)}
                    {event.metadata?.trigger_lane_type === v && ' ✅'}
                  </Button>
                ))}
              </ButtonsBox>
            </>
          )}
        </ImageMetadataSettingsSelectorWrapper>
      );
    }
    return <ImageMetadataSettingsSelectorWrapper>No metadata to display </ImageMetadataSettingsSelectorWrapper>;
  }

  return {
    getImageWithMetadataParameters,
    MetadataControls,
  };
};
